import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Domain, ERegistrars, ICNAMERecord, IMXRecord } from '@feathr/blackbox';
import { toast } from '@feathr/components';
import { errorMessage } from '@feathr/hooks';

export type TDomainRecord = ICNAMERecord | IMXRecord;

function isDomainRecord(value: unknown): value is TDomainRecord {
  return typeof value === 'object' && value !== null && 'type' in value;
}

export interface IUseDomainRecords {
  isLoading: boolean;
  records: TDomainRecord[];
}

/** Fetches the domain's DNS records in the given registrar format, flattened into one list. */
export function useDomainRecords(domain: Domain, format: ERegistrars): IUseDomainRecords {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [records, setRecords] = useState<TDomainRecord[]>([]);

  useEffect(() => {
    let isCurrent = true;

    async function getRecords(): Promise<void> {
      setIsLoading(true);
      try {
        const response = await domain.formatRecords({ format });
        // The response also carries non-record values, which would render as blank rows.
        const flattened: unknown[] = Object.values(response).flat();
        if (isCurrent) {
          setRecords(flattened.filter(isDomainRecord));
        }
      } catch (e) {
        if (isCurrent) {
          setRecords([]);
        }
        toast(
          t('There was an error formatting your records: {{- message}}', {
            message: errorMessage(e, t),
          }),
          { type: 'error' },
        );
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    }

    void getRecords();

    return () => {
      isCurrent = false;
    };
  }, [domain, format, t]);

  return { isLoading, records };
}
